import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import React, { useRef, useEffect, useContext } from 'react';
import classnames from 'classnames';
import omitBy from 'lodash/omitBy';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import ArrowRightIcon from '@wix/da-ds/pkg/Icons/ArrowRight';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import Flex from '@wix/da-ds/pkg/Flex';
import NewBadge from '@wix/da-ds/pkg/Badges/NewBadge';
import ListItemButton from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemButton';
import StandardMenuSubmenu from '../StandardMenuSubmenu';
import StandardMenuContext from '../context';
import s from './StandardMenuOption.scss';
/**
 * A parent of StandardMenuOption could be a multitude of roles. Each parent role may require a certain role be applied to this component.
 *
 * This returns the correct role for the supplied `parentRole`.
 */ function getRoleBasedOnParent(// The `string | undefined` part here is to get around some typing issues
parentRole) {
    switch(parentRole){
        case 'listbox':
            return 'option';
        case 'menu':
            return 'menuitem';
        default:
            break;
    }
}
const StandardMenuOption = ({ id, item, item: { icon: itemIcon, iconClassName, suffix: suffixProp, onClick, onMouseEnter, disabled, danger, className: itemClassName, value, highlighted, selected, label, subLabel, boldLabel, biData, dataHook, tabIndex, link, target, title, submenu, subMenuPreferredSide, estimatedSubmenuWidth, minimumHeightNeededForSubmenu }, hideCheckIcon, withHoverEffect, className, focused })=>{
    const { role: parentRole } = useContext(StandardMenuContext);
    let icon = null;
    if (itemIcon) {
        icon = /*#__PURE__*/ React.cloneElement(itemIcon, {
            className: iconClassName
        });
    }
    const itemRef = useRef(null);
    useEffect(()=>{
        if (itemRef.current && focused) {
            itemRef.current.focus({
                preventScroll: true
            });
        }
    }, [
        itemRef,
        focused
    ]);
    const prefix = icon;
    const suffix = submenu ? /*#__PURE__*/ React.createElement(ArrowRightIcon, {
        size: IconSize.SMALLER,
        className: s['submenu-arrow']
    }) : suffixProp;
    const content = /*#__PURE__*/ React.createElement(ListItemButton, {
        className: itemClassName,
        fillContainerWidth: true,
        disabled: disabled,
        isDangerous: danger,
        prefix: prefix,
        suffix: suffix,
        isLabelBold: selected || boldLabel,
        label: item.complexLabel || /*#__PURE__*/ React.createElement(Flex, {
            alignItems: "center"
        }, item.label, item.newTag && /*#__PURE__*/ React.createElement(NewBadge, {
            className: s['new-tag']
        })),
        subLabel: subLabel
    });
    // omitBy to prevent undefined values from overwriting props that we need.
    const sharedProps = omitBy({
        id,
        innerRef: (node)=>{
            itemRef.current = node;
        },
        className: classnames(s['menu-item'], // TODO: address via 6.1 ?
        highlighted && s['highlighted'], className),
        value,
        disabled,
        selected,
        label: typeof label === 'string' ? label : undefined,
        onClick: disabled ? undefined : onClick,
        onMouseEnter,
        biData: biData,
        // We don't want to tab into items individually, the wrapping StandardMenuContent will manage
        // focus itself.
        tabIndex: tabIndex === undefined ? -1 : tabIndex,
        children: content,
        title,
        role: getRoleBasedOnParent(parentRole),
        'data-hook': dataHook,
        'data-testid': 'menu-option',
        // Undefined on false to avoid some extra text being read out in VoiceOver + Chrome
        'aria-selected': selected ? true : undefined,
        'aria-disabled': disabled,
        'data-selected': selected ? 'selected' : undefined
    }, (keyValue)=>keyValue === undefined);
    if (link) {
        return /*#__PURE__*/ React.createElement(BiLink, _object_spread({
            href: link,
            target: target
        }, sharedProps));
    } else if (submenu && !disabled) {
        // A11Y TODO: Figure out what role these should have
        // Should likely be the usual `role="getRoleBasedOnParent(...)"` with an `aria-haspopup`
        // https://www.digitala11y.com/option-role/
        return /*#__PURE__*/ React.createElement(StandardMenuSubmenu, _object_spread({
            submenu: submenu,
            preferredSide: subMenuPreferredSide,
            estimatedSubmenuWidth: estimatedSubmenuWidth,
            minimumHeightNeeded: minimumHeightNeededForSubmenu
        }, sharedProps));
    } else {
        return /*#__PURE__*/ React.createElement(PlainButtonOrLink, _object_spread({
            isFakeButton: true
        }, sharedProps));
    }
};
StandardMenuOption.displayName = 'StandardMenuOption';
export default StandardMenuOption;
